import { env } from "@/lib/env";

/**
 * Central leveled logger. Quiet by default; raise verbosity with
 * NEXT_PUBLIC_LOG_LEVEL. Structured context is attached per call. PII/token
 * redaction happens here so callers never have to think about it.
 * (ENGINEERING.md §6 — full Sentry sink lands in Phase 1.)
 */
type Level = "debug" | "info" | "warn" | "error";

const ORDER: Record<Level | "silent", number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
};

const REDACT_KEYS = /^(authorization|token|password|secret|cookie)$/i;

function redact(context?: Record<string, unknown>) {
  if (!context) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(context)) {
    out[k] = REDACT_KEYS.test(k) ? "[redacted]" : v;
  }
  return out;
}

function enabled(level: Level) {
  return ORDER[level] >= ORDER[env.logLevel];
}

function emit(
  level: Level,
  message: string,
  context?: Record<string, unknown>,
) {
  if (!enabled(level)) return;
  const payload = redact(context);
  // eslint-disable-next-line no-console
  const fn = console[level] ?? console.log;
  if (payload) fn(`[${level}] ${message}`, payload);
  else fn(`[${level}] ${message}`);
}

export const logger = {
  debug: (msg: string, ctx?: Record<string, unknown>) =>
    emit("debug", msg, ctx),
  info: (msg: string, ctx?: Record<string, unknown>) => emit("info", msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) => emit("warn", msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) =>
    emit("error", msg, ctx),
};
